'use server'

import { redirect } from 'next/navigation'

import { clearA11yCookie } from '@/lib/a11y/cookie'
import { loginFormSchema, registerFormSchema } from '@/lib/auth/schemas'
import { clearSessionCookie, establishSession } from '@/lib/auth/session'
import {
  strapiLogin,
  strapiRegister,
  strapiSavePreferences,
  strapiUserMe,
} from '@/lib/auth/strapi-auth'

export interface AuthActionResult {
  ok: boolean
  message?: string
}

export async function registerUser(formData: FormData): Promise<AuthActionResult> {
  const parsed = registerFormSchema.safeParse({
    firstName: String(formData.get('firstName') ?? ''),
    lastName: String(formData.get('lastName') ?? ''),
    username: String(formData.get('username') ?? ''),
    email: String(formData.get('email') ?? ''),
    password: String(formData.get('password') ?? ''),
    confirmPassword: String(formData.get('confirmPassword') ?? ''),
  })
  if (!parsed.success) {
    return { ok: false, message: parsed.error.issues[0]?.message ?? 'Nevažeći podaci' }
  }

  try {
    const auth = await strapiRegister(parsed.data.username, parsed.data.email, parsed.data.password)
    // Ime i prezime se čuvaju posebno (register ne prima dodatna polja).
    await strapiSavePreferences(auth.jwt, {
      firstName: parsed.data.firstName,
      lastName: parsed.data.lastName,
    })
    const user = await strapiUserMe(auth.jwt)
    await establishSession(auth.jwt, user)
    return { ok: true }
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : 'Greška pri registraciji',
    }
  }
}

export async function loginUser(formData: FormData): Promise<AuthActionResult> {
  const parsed = loginFormSchema.safeParse({
    email: String(formData.get('email') ?? ''),
    password: String(formData.get('password') ?? ''),
  })
  if (!parsed.success) {
    return { ok: false, message: parsed.error.issues[0]?.message ?? 'Nevažeći podaci' }
  }

  try {
    const auth = await strapiLogin(parsed.data.email, parsed.data.password)
    const user = await strapiUserMe(auth.jwt)
    await establishSession(auth.jwt, user)
    return { ok: true }
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : 'Greška pri prijavi' }
  }
}

export async function logoutUser(): Promise<void> {
  await clearSessionCookie()
  await clearA11yCookie()
  redirect('/')
}
